import { format } from "date-fns";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import type { WsAccount } from "@storyboard/web-client";
import FileFragment from "@/components/FeedItem/FileFragments";
import LinkFragment from "@/components/LinkFragment";
import { ampli } from "@/config";
import useOnScreen from "@/hooks/useOnScreen";
import { useAccountLookup } from "@/hooks/workspace/useAccountLookup";
import { ActionContext } from "@/models/ActionsProvider";
import { AppContext } from "@/models/AppStateProvider";
import { AudioAppContext } from "@/models/AudioAppContextProvider";
import { DataContext } from "@/models/DataProvider";
import { FeedContext, type FullItem } from "@/models/FeedContextProvider";
import { type AllPreferredLanguage, Languages } from "@/models/languages";
import { lookupAccountById } from "@/models/repositories";
import { CurrentFeedContext } from "@/models/StateProviders/currentFeedProvider";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { actions, TelemetryContext } from "@/models/TelemetryProvider";
import { UxContext } from "@/models/UxStateProvider";
import type { Feed } from "@/store/types";
import { transformEvents } from "@/transformers/transformEvents";
import { randomString, selectAudio, selectTranscription, targetCodecForBrowser } from "@/utils";

type UseFeedItemDataArgs = {
  item: FullItem;
  feed: Feed;
  active?: boolean;
  highlightPodcast?: boolean;
  preventScroll?: (value: boolean) => void;
  aliasChannelOwnerId?: string;
};

export function useFeedItemData({
  item,
  feed,
  active,
  highlightPodcast,
  preventScroll,
  aliasChannelOwnerId,
}: UseFeedItemDataArgs) {
  const { finishAction } = useContext(TelemetryContext);
  const { flags } = useContext(AppContext);
  const { preferredLanguage } = useContext(DataContext);
  const { accountEvent } = useContext(ActionContext);
  const { isCurrentFeedAdmin, haveWritePermission } = useContext(CurrentFeedContext);
  const { myAccount } = useContext(MyAccountContext);
  const { playerPlaying, activeItemId } = useContext(AudioAppContext);
  const {
    isSmUp,
    userReadOnlyMode,
    setFleetMessageActiveSection,
    setFleetMessageModalOpen,
    setFleetMessageLongitude,
    setFleetMessageLatitude,
    setFleetMessageFromItem,
    setFleetMessageText,
    setFleetMessageInvalidLocation,
  } = useContext(UxContext);
  const { itemTranscriptState } = useContext(FeedContext);
  const accountOf = useAccountLookup();

  const [isProcessing, setIsProcessing] = useState<boolean>(true);
  const [missingAccount, setMissingAccount] = useState<WsAccount | null>(null);
  const [showModalTranscription, setShowModalTranscription] = useState<boolean>(false);
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState<boolean>(false);
  const [debuggerOpen, setDebuggerOpen] = useState<boolean>(false);
  const [displayArtifact, setDisplayArtifact] = useState<string>("");
  const [feedItemId] = useState(`feedItem-${feed?.id}-${item?.id}-${randomString(5)}`);
  const mine = item.accountId === myAccount?.id;
  const unread = item.unread;
  const transcriptions = item.transcriptions;
  const audioContents = item.audioEncodings;
  const rawFiles = item.files;
  const rawLinks = item.links;
  const displayArtifactRecord = item.displayArtifact;
  const callRecords = item.callRecords;
  const hasCallRecord = callRecords && callRecords.length > 0;
  const senderAccount = item.account;

  useEffect(() => {
    if (!item?.account && !missingAccount?.id) {
      lookupAccountById(item.accountId).then((response: WsAccount) => {
        if (response?.id) {
          setMissingAccount(() => response);
        }
      });
    }
  }, [item?.accountId, missingAccount?.id]);

  const { count, groups, allDriverEvents } = useMemo(
    () =>
      transformEvents({
        aliasChannelOwnerId,
        processing: isProcessing,
        item,
        accountOf,
      }),
    [isProcessing, item.events, item.accountId, accountOf, aliasChannelOwnerId],
  );

  const files =
    rawFiles?.length > 0
      ? rawFiles.map((file) => ({
          ...file,
          fragment: FileFragment(file),
        }))
      : [];

  const links =
    rawLinks?.length > 0
      ? rawLinks.map((link) => ({
          ...link,
          fragment: LinkFragment(link),
        }))
      : [];

  const { simpleHtml, richTranscript, textTranscript } = selectTranscription(preferredLanguage, transcriptions);
  const transcriptionContent = simpleHtml || richTranscript || textTranscript;
  const inputLanguage = transcriptions?.filter((t) => !t.translatedFrom)[0]?.language as AllPreferredLanguage;

  const targetCodec = targetCodecForBrowser();

  const suitableAudioContent = useMemo(
    () =>
      selectAudio(
        preferredLanguage,
        audioContents?.filter((a) => a.codec === targetCodec),
        inputLanguage,
      ),
    [audioContents, inputLanguage, preferredLanguage, targetCodec],
  );

  const originalAudio = audioContents?.find((a) => a?.translatedFrom === null);
  const firstTranslatedFrom = [...new Set(transcriptions?.map((item) => item.translatedFrom)?.filter((item) => item))];
  const transcriptTranslatedFrom = firstTranslatedFrom?.length > 0 ? Languages[firstTranslatedFrom[0]] : null;

  const audioContent = suitableAudioContent[0];

  const duration: number =
    Number(audioContent?.duration) ||
    Number(
      audioContents?.filter((a) => a.codec !== targetCodec && a.duration && a.language === audioContent?.language)[0]
        ?.duration,
    ) ||
    0;

  const hasPlayableAudio = suitableAudioContent.length > 0;
  const hasTranscription = !!transcriptionContent || transcriptions?.length > 0;
  const noTranscriptionContent = (hasTranscription && !transcriptionContent) || transcriptions?.length === 0;
  const hasFiles = rawFiles?.length > 0;
  const hasLinks = rawLinks?.length > 0;
  const filesOrLinks = hasFiles || hasLinks;
  const shouldShowAudioTranscriptContent = !hasCallRecord;
  const hasContent = !!(filesOrLinks || hasPlayableAudio || hasTranscription || hasCallRecord || displayArtifactRecord);
  // The card holds a placeholder only while content is still on its way: not yet downloaded, or still being produced
  // by the pipeline. A loaded item that has nothing shows an empty card, as before, rather than a skeleton forever.
  // Derived in render, not from `isProcessing`, so the first paint of a loaded item never shows a placeholder.
  const contentPending = !hasContent && (!item.loadedContent || item.status === "Processing");

  useEffect(() => {
    setIsProcessing(!hasContent);
  }, [hasContent]);

  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(ref, {
    root: document.querySelector("#vListRef"),
    threshold: 1,
    trackVisibility: true,
    delay: 100,
  });

  useEffect(() => {
    if (hasPlayableAudio) {
      finishAction(actions.feedItemAudioTts(item));
    }
    if (hasTranscription) {
      finishAction(actions.feedItemTextTts(item));
      finishAction(actions.bootstrapFull());
      finishAction(actions.feedNavigate(item.feedId));
    }
  }, [hasPlayableAudio, hasTranscription, item, item.feedId, finishAction]);

  const showTranscription = itemTranscriptState?.includes(item?.id);

  // The VAD-based reliability gate read pipeline artifact metadata, which is deprecated and no longer synced to web.
  const unreliableTranscription = false;

  const unreliableTranscriptLabel = `This transcription ${
    noTranscriptionContent ? "is not available." : "might be unreliable."
  }`;

  const handleSetConfirmDeleteDialog = useCallback((value: boolean) => setConfirmDeleteDialog(value), []);
  const handleSetDebuggerOpen = useCallback((value: boolean) => setDebuggerOpen(value), []);

  const handlePlay = useCallback(
    async (time: number) => {
      ampli.playFeedItemAudio({ feedId: feed.id, feedItemId: item.id });
      accountEvent("Started Listening to Feed Item", {
        feedId: feed.id,
        feedItemId: item.id,
        data: { time },
      });
    },
    [feed.id, item.id],
  );

  const handleFinished = useCallback(async () => {
    accountEvent("Finished Listening to Feed Item", {
      feedId: feed.id,
      feedItemId: item.id,
    });
  }, [accountEvent, feed.id, item]);

  const handlePause = useCallback(
    async (time: number) => {
      accountEvent("Paused Feed Item", {
        feedId: feed.id,
        feedItemId: item.id,
        data: { time },
      });
    },
    [accountEvent, feed.id, item],
  );

  useEffect(() => {
    if (item.hasRegisteredSeenEvent) {
      return;
    }
    const fireEvent = isVisible && !isProcessing && !userReadOnlyMode;
    if (fireEvent) {
      accountEvent("Saw Feed Item", {
        feedId: item.feedId,
        feedItemId: item.id,
      });
    }
  }, [isVisible, isProcessing, userReadOnlyMode, item?.id, item?.feedId, item.hasRegisteredSeenEvent]);

  const accountName = useMemo(() => {
    if (isProcessing) return null;
    if (!senderAccount && missingAccount?.name) return missingAccount.name;
    if (!senderAccount) return `Deactivated user: ${item.accountId}`;
    return !mine ? senderAccount?.name : "Me";
  }, [mine, isProcessing, senderAccount, missingAccount]);

  const itemDate = item?.createdAt ? format(new Date(item.createdAt), "MMM d, yyyy, h:mm aa") : "";

  const MenuOptions = {
    showOptions: isSmUp,
    showMessageDetails: (mine || isCurrentFeedAdmin) && !isProcessing && count > 0,
    canDelete: (mine || isCurrentFeedAdmin) && haveWritePermission,
    canCopy: item?.url,
    canCopyTranscript: (mine || isCurrentFeedAdmin) && textTranscript,
    canSendFleetMessage: callRecords?.length === 0 && item?.account?.role === "limitedMember",
  };

  const shouldRender = !!(
    window &&
    (audioContent ||
      transcriptionContent ||
      files ||
      links ||
      hasCallRecord ||
      displayArtifactRecord ||
      item.status === "Processing")
  );

  const highlightShowMorePodcastLabel = displayArtifactRecord && (highlightPodcast || active);
  const messageWrapperLabel = `${accountName} at ${itemDate}, click to play`;

  const feedItemModalsProps = {
    debugMode: !!flags.debugMode,
    debuggerOpen,
    setDebuggerOpen: handleSetDebuggerOpen,
    confirmDeleteDialog,
    setConfirmDeleteDialog: handleSetConfirmDeleteDialog,
    preventScroll,
    showModalTranscription,
    setShowModalTranscription,
    feedItemId,
    hasPlayableAudio,
    duration,
    itemId: item.id,
    itemFeedId: item.feedId,
    handlePlay,
    handleFinished,
    handlePause,
    preferredLanguage,
    transcriptions,
  };

  // The refresh endpoint has been dead for a while (the old client method was an empty body); the
  // spinner survives as user feedback until the pipeline pushes an updated item.
  const refreshPipeline = async () => {
    setIsProcessing(true);
    setTimeout(() => setIsProcessing(false), 3000);
  };

  return {
    flags,
    mine,
    unread,
    senderAccount,
    transcriptions,
    audioContents,
    displayArtifactRecord,
    callRecords,
    hasCallRecord,
    isProcessing,
    setIsProcessing,
    count,
    groups,
    allDriverEvents,
    files,
    links,
    textTranscript,
    transcriptionContent,
    hasPlayableAudio,
    hasTranscription,
    filesOrLinks,
    contentPending,
    shouldShowAudioTranscriptContent,
    duration,
    showTranscription,
    showModalTranscription,
    setShowModalTranscription,
    unreliableTranscription,
    unreliableTranscriptLabel,
    transcriptTranslatedFrom,
    ref,
    accountName,
    itemDate,
    handlePlay,
    handleFinished,
    handlePause,
    confirmDeleteDialog,
    handleSetConfirmDeleteDialog,
    debuggerOpen,
    handleSetDebuggerOpen,
    displayArtifact,
    setDisplayArtifact,
    feedItemId,
    MenuOptions,
    shouldRender,
    highlightShowMorePodcastLabel,
    messageWrapperLabel,
    feedItemModalsProps,
    refreshPipeline,
    playerPlaying,
    activeItemId,
    isCurrentFeedAdmin,
    userReadOnlyMode,
    isSmUp,
    setFleetMessageActiveSection,
    setFleetMessageModalOpen,
    setFleetMessageLongitude,
    setFleetMessageLatitude,
    setFleetMessageFromItem,
    setFleetMessageText,
    setFleetMessageInvalidLocation,
    preferredLanguage,
  };
}
